/**
 * User listing record — matches the shape returned by the `accounts/paginate` endpoint.
 */
export interface User {
    id: string;
    tenantUserId: string;
    firstName: string;
    lastName: string;
    middleName: string;
    email: string;
    userName: string;
    phoneNumber: string;
    userStatusId: number;
    roleId: string;
    roleName: string;
    identityProvider: string;
    /** ISO 8601 timestamp string — formatted at render time. */
    lastLoginDate: string | null;
    signUpDate: string;
    titleId: number;
    genderId: number;
    isMFAEnabled: boolean;
    isTOTPEnforced: boolean;
    isEmailEnforced: boolean;
    emailConfirmed: boolean;
    portalTypeId?: number;
    canInvite?: boolean;
    isEmailVerified?: boolean;
    isPhoneNumberVerified?: boolean;
    dateOfBirth: string | null;
    externalId: string | null;
    moreInfo: string | null;
    providerType: number;
    defaultProviderId: number;
}

export enum UserStatusEnum {
    Active = 1,
    Inactive = 2,
    Invited = 3,
}

export type UserStatus = 'active' | 'inactive' | 'invited';

export const USER_STATUSES: ReadonlyArray<UserStatus> = ['active', 'inactive', 'invited'];

export function userStatusFromId(userStatusId: number): UserStatus {
    switch (userStatusId) {
        case UserStatusEnum.Active:
            return 'active';
        case UserStatusEnum.Inactive:
            return 'inactive';
        case UserStatusEnum.Invited:
            return 'invited';
        default:
            return 'active';
    }
}

export function userStatusToId(status: UserStatus): number {
    switch (status) {
        case 'active':
            return UserStatusEnum.Active;
        case 'inactive':
            return UserStatusEnum.Inactive;
        case 'invited':
            return UserStatusEnum.Invited;
    }
}

/**
 * Status filter dropdown options.
 */
export interface UserStatusFilterOption {
    id: UserStatus | 'all';
    labelKey: string;
}

export const USER_STATUS_FILTER_OPTIONS: ReadonlyArray<UserStatusFilterOption> = [
    { id: 'all', labelKey: 'users.filters.allStatuses' },
    { id: 'active', labelKey: 'users.status.active' },
    { id: 'inactive', labelKey: 'users.status.inactive' },
    { id: 'invited', labelKey: 'users.status.invited' },
];

export enum UserRoleEnum {
    ExecutiveAdmin = '8d6bc02f-acbf-4385-d2d7-08dee714dc95',
    OperationalAdmin = '5092da72-b27c-4a71-cd0e-08dee7151a8d',
    User = 'd62169b2-7174-47cd-d7f0-08dee7ce0d8a',
    SaasAdmin = 'bd8a91b5-33a0-4d99-9c67-07cf15688aaa',
}

export enum FeedbackTypeEnum {
    GeneralFeedback = 0,
    BugReport = 1,
    Other = 2,
}

/**
 * Draft submitted by the Invite User modal. The real API may require more
 * (role, tenant, etc.) — we'll extend this interface when wiring it up.
 */
export interface InviteUserDraft {
    email: string;
    mobile: string;
    firstName: string;
    lastName: string;
    roleId: UserRoleEnum;
}

export interface InviteUserRequest {
    email: string;
    phoneNumber: string;
    userName: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    portalTypeId?: number;
    roleId?: string;
    identityProvider?: string;
    canInvite?: boolean;
    userStatusId?: number;
    moreInfo?: string;
    isEmailVerified?: boolean;
    isPhoneNumberVerified?: boolean;
}

export interface UpdateUserRequest extends InviteUserRequest {
    id: string;
}

export interface UpdateUserStatusRequest {
    statusId: number;
}

export interface SetupPasswordRequest {
    token: string;
    email: string;
    password: string;
    verifyTypeId: number;
}
